import moment from "moment";
import { DateTime } from "types";
import { DashboardTimePeriod } from "types/Dashboard";

class TimeHelper {
  static formatCalendarTime(datetime: DateTime, formats?: moment.CalendarSpec) {
    return moment(datetime).calendar(undefined, formats);
  }

  static getDiff(startTime?: DateTime, endTime?: DateTime) {
    if (!(startTime && endTime)) return null;

    const start = moment(startTime);
    const end = moment(endTime);
    const diff = moment.utc(end).diff(moment.utc(start));

    /**
     * @note
     * `HH:mm` is 24 hour time
     * `hh:mm` is 12 hour time
     */

    return moment.utc(diff).format("HH:mm");
  }

  static getRelativePeriodDates(
    relativePeriod: DashboardTimePeriod["relativePeriod"],
    params?: { endDate?: DateTime }
  ) {
    const duration = moment.duration(relativePeriod);

    if (!["P1D", "P7D", "P30D", "P90D", "P365D"].includes(`${relativePeriod}`)) {
      return null;
    }

    // Today end of Day
    const end = moment(params?.endDate).endOf("day");
    // Subtract duration from end of day
    const start = end.clone().subtract(duration, "day").startOf("day");

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  }

  static getDateRanges(relativePeriod: "P365D" | "P90D" | "P30D" | "P7D") {
    const { unit, count } = {
      P7D: { unit: "day" as const, count: 7 },
      P30D: { unit: "day" as const, count: 30 },
      P90D: { unit: "week" as const, count: 13 }, // 12.85 weeks in 90 days
      P365D: { unit: "month" as const, count: 12 },
    }[relativePeriod];

    // Set as start of the first period
    const firstPeriodStartDate = moment().add(1, unit).startOf(unit).subtract(count, unit);
    // Set as end of the first period
    const firstPeriodEndDate = firstPeriodStartDate.clone().endOf(unit);

    return new Array(count)
      .fill({
        startDate: firstPeriodStartDate.toISOString(),
        endDate: firstPeriodEndDate.toISOString(),
      })
      .map(({ startDate, endDate }: { startDate: string; endDate: string }, index) => {
        const isLast = index === count - 1;
        return {
          // .add(2, "hour").startOf("day") is to avoid DST issues
          startDate: moment(startDate).add(2, "hour").startOf("day").add(index, unit).toISOString(),
          endDate: (isLast
            ? moment().endOf("day")
            : // .subtract(2, "hour").endOf("day") is to avoid DST issues
              moment(endDate).subtract(2, "hour").endOf("day").add(index, unit).endOf(unit)
          ).toISOString(),
        };
      });
  }

  /**
   * Formats a duration in seconds as:
   * - "mm:ss" for durations under 1 hour
   * - "hh:mm:ss" for durations of 1 hour or more
   */
  static formatTime(restPeriod: number): string {
    const duration = moment.duration(restPeriod, "seconds");

    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    const hh = hours.toString().padStart(2, "0");
    const mm = minutes.toString().padStart(2, "0");
    const ss = seconds.toString().padStart(2, "0");

    return hours > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
  }

  /**
   * Get the next Monday from today.
   * If today is Monday, return today.
   * @returns Date string in YYYY-MM-DD format
   */
  static getNextMonday(): string {
    const today = moment();
    const dayOfWeek = today.day();

    if (dayOfWeek === 1) {
      return today.format("YYYY-MM-DD");
    }

    // For Sunday (0), next Monday is day 1 of this week (tomorrow)
    // For Tue-Sat (2-6), next Monday is day 8 (day 1 of next week)
    const nextMondayDay = dayOfWeek === 0 ? 1 : 8;
    const nextMonday = today.clone().day(nextMondayDay);
    return nextMonday.format("YYYY-MM-DD");
  }
}

export default TimeHelper;
